import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { useSnackbar } from 'notistack';
import { change, Field, reduxForm, reset } from 'redux-form';

import { PayPalButton } from 'react-paypal-button-v2';
import {
  CardElement,
  Elements,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';

import { Typography } from '@material-ui/core';

import { useMutation } from '@apollo/react-hooks';

import {
  PAYPAL,
  STRIPE,
  PAY_ON_ARRIVAL,
  WIDGET_ROOT,
  WIDGET_WRAPPER_ID
} from 'constants/widget';

import { CREATE_ACCOMMODATION_BOOKING } from 'graphql/mutations/accommodations-bookings';
import { CREATE_ACTIVITY_BOOKING } from 'graphql/mutations/activities-bookings';
import { UPDATE_BOOKING_PAYMENT } from 'graphql/mutations/booking-payment';

import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Radio from '@material-ui/core/Radio';

import { getPreparedBookAccommodationData } from 'utils/book-accommodation/prepareBookAccommodationData';
import { getPreparedBookActivityData } from 'utils/book-activity/prepareBookActivityData';
import { getWidgetTotalPrice } from 'utils/widget/getTotalPrice';
import { getAvailablePaymentsTypes } from 'utils/widget/getAvailablePaymentsTypes';

import BookingInformation from '../booking-information/BookingInformation';

import { Input } from 'components';
import { BookingConfirmDialog } from '../booking-confirm-dialog/BookingConfirmDialog';

import { LOCALES_OBJ, PAYPAL_LOCALES_OBJ } from 'constants/base';

import validate from './validate';

import { useStyles, radioBtnStyles } from './styles';

const UserInformation = () => {
  const classes = useStyles();
  const [t] = useTranslation();

  return (
    <div className={classes.userSection}>
      <Typography variant="h3" classes={{ root: classes.sectionTitleIndent }}>
        {t('Your information')}
      </Typography>
      <div className={classes.row}>
        <Field
          name="firstName"
          label="First name"
          placeholder="First name"
          className={classes.field}
          labelClass={classes.inputLabel}
          component={Input}
        />
        <Field
          name="lastName"
          label="Last name"
          placeholder="Last name"
          className={classes.field}
          labelClass={classes.inputLabel}
          component={Input}
        />
      </div>
      <div className={classes.row}>
        <Field
          name="phone"
          label="Phone"
          placeholder="Phone"
          className={classes.field}
          labelClass={classes.inputLabel}
          isOnlyNumber={true}
          isPhoneNumber={true}
          maxLength={20}
          component={Input}
        />
        <Field
          name="email"
          label="Email"
          placeholder="Email"
          className={classes.field}
          labelClass={classes.inputLabel}
          component={Input}
        />
      </div>
    </div>
  );
};

const BillingInformation = () => {
  const classes = useStyles();
  const [t] = useTranslation();

  return (
    <div className={classes.userSection}>
      <Typography variant="h3" classes={{ root: classes.sectionTitleIndent }}>
        {t('Billing information')}
      </Typography>
      <div className={classes.row}>
        <Field
          name="address"
          label="Address"
          placeholder="Address"
          className={classes.fieldFullWidth}
          labelClass={classes.inputLabel}
          component={Input}
        />
      </div>
      <div className={classes.row}>
        <Field
          name="city"
          label="City"
          placeholder="City"
          className={classes.longField}
          labelClass={classes.inputLabel}
          component={Input}
        />
        <Field
          name="zip"
          label="Zip"
          placeholder="Zip"
          className={classes.shortField}
          labelClass={classes.inputLabel}
          isOnlyNumber={true}
          maxLength={12}
          component={Input}
        />
      </div>
    </div>
  );
};

const CreditCardInformation = ({
  settingsData,
  paymentType,
  isSubmit,
  setIsSubmit,
  submitCallback,
  currency,
  totalPrice,
  changeFormFieldValue
}) => {
  const classes = useStyles();
  const [t, i18n] = useTranslation();
  const radioBtnSClasses = radioBtnStyles();

  const selectedLanguage = i18n.language;

  const paymentsTypes = getAvailablePaymentsTypes(settingsData);

  const [isInit, setIsInit] = React.useState(true);

  React.useEffect(() => {
    if (isInit) {
      setIsInit(false);
      return;
    }
    const _paymentType = paymentType;
    changeFormFieldValue('paymentType', '');
    setTimeout(() => {
      changeFormFieldValue('paymentType', _paymentType);
    });
  }, [selectedLanguage]);

  return (
    <div>
      <Typography variant="h3" classes={{ root: classes.sectionTitleIndent }}>
        {t('Payment method')}
      </Typography>

      {paymentsTypes.map((item, index) => {
        return (
          <div key={index}>
            <Accordion
              expanded={paymentType === item}
              classes={{ root: classes.expansionPanelRoot }}
            >
              <AccordionSummary
                classes={{
                  root: classes.expPanelSummaryRoot,
                  expanded: classNames(classes.expPanelSummaryExpanded, {
                    [classes.emptyExpPanelSummaryExpanded]:
                      item === PAY_ON_ARRIVAL
                  }),
                  content: classes.expPanelSummaryContent
                }}
                onClick={() => changeFormFieldValue('paymentType', item)}
                aria-label="Expand"
                aria-controls="additional-actions1-content"
                id="additional-actions1-header"
              >
                <FormControlLabel
                  control={
                    <Radio
                      color="default"
                      classes={radioBtnSClasses}
                      checked={paymentType === item}
                    />
                  }
                  label={
                    <Typography variant={'h3'} className={classes.expLabel}>
                      {t(item)}{' '}
                      {item !== PAY_ON_ARRIVAL && (
                        <span className={classes.fee}>
                          {t('Transaction fee may apply')}
                        </span>
                      )}
                    </Typography>
                  }
                />
              </AccordionSummary>
              <AccordionDetails
                classes={{
                  root: classNames(classes.expansionPanelDetailsRoot, {
                    [classes.emptyExpansionPanelDetailsRoot]:
                      item === PAY_ON_ARRIVAL
                  })
                }}
              >
                {item === STRIPE && paymentType === STRIPE && (
                  <MemoStripePayment
                    isSubmit={isSubmit}
                    setIsSubmit={setIsSubmit}
                    submitCallback={submitCallback}
                    settingsData={settingsData}
                    selectedLanguage={selectedLanguage}
                  />
                )}
                {item === PAYPAL && paymentType === PAYPAL && (
                  <MemoPayPalPayment
                    currency={currency}
                    totalPrice={totalPrice}
                    submitCallback={submitCallback}
                    settingsData={settingsData}
                    selectedLanguage={selectedLanguage}
                  />
                )}
              </AccordionDetails>
            </Accordion>
          </div>
        );
      })}
    </div>
  );
};

const PayPalPayment = ({
  currency,
  totalPrice,
  submitCallback,
  settingsData,
  selectedLanguage
}) => {
  const onSuccessHandler = (details, data) => {
    submitCallback(null, data);
  };

  return (
    <PayPalButton
      amount={totalPrice}
      onSuccess={onSuccessHandler}
      options={{
        clientId: settingsData.payPalSettings.clientId,
        currency: `${currency}`,
        locale: PAYPAL_LOCALES_OBJ[selectedLanguage]
      }}
      style={{
        layout: 'horizontal',
        height: 33,
        color: 'blue',
        tagline: false
      }}
    />
  );
};
const MemoPayPalPayment = React.memo(PayPalPayment);

const StripePayment = ({
  isSubmit,
  setIsSubmit,
  submitCallback,
  settingsData,
  selectedLanguage
}) => {
  return (
    <Elements
      stripe={loadStripe(settingsData.stripeSettings.publishableKey, {
        locale: LOCALES_OBJ[selectedLanguage]
      })}
    >
      <CardElementWrapper
        isSubmit={isSubmit}
        setIsSubmit={setIsSubmit}
        submitCallback={submitCallback}
      />
    </Elements>
  );
};
const MemoStripePayment = React.memo(StripePayment);

const CardElementWrapper = ({ isSubmit, setIsSubmit, submitCallback }) => {
  const { enqueueSnackbar } = useSnackbar();

  const stripe = useStripe();
  const elements = useElements();

  React.useEffect(() => {
    if (!isSubmit) return;
    submit();
  }, [isSubmit]);

  const submit = async () => {
    const cardElement = elements.getElement(CardElement);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement
    });

    if (error) {
      enqueueSnackbar(error.message, {
        variant: 'error'
      });
      setIsSubmit(false);
    } else {
      submitCallback(paymentMethod);
    }
  };

  return (
    <CardElement
      options={{
        style: {
          base: {
            fontSize: '13px',
            lineHeight: '18px',
            '::placeholder': {
              color: '#A2A2A2'
            }
          }
        }
      }}
    />
  );
};

const Payment = ({
  resetWidgetForm,
  widgetForm,
  productData,
  handleSubmit,
  setIsSubmit,
  isSubmit,
  isAccommodationBooking,
  settingsData,
  changeFormFieldValue
}) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const wrapperRef = useRef(null);
  const paymentType = widgetForm ? widgetForm.values.paymentType : '';
  const currency = widgetForm ? widgetForm.values.currency : '';
  const isPrivacy = widgetForm.values.isPrivacy || false;

  const [totalPrice, setTotalPrice] = React.useState(0);
  const [payPalData, setPayPalData] = React.useState(null);
  const [stripeData, setStripeData] = React.useState(null);
  const [clientSecret, setClientSecret] = React.useState('');
  const [activityBookingId, setActivityBookingId] = React.useState(0);
  const [accommodationBookingId, setAccommodationBookingId] = React.useState(
    ''
  );
  React.useEffect(() => {
    if (clientSecret) {
      window.removeEventListener('message', handleEventMessage);
      window.removeEventListener('msg', handleEventMessage);
      handleStripeAuth();
    }
  }, [clientSecret]);
  const [createAccommodationBooking] = useMutation(
    CREATE_ACCOMMODATION_BOOKING
  );
  const [createActivityBooking] = useMutation(CREATE_ACTIVITY_BOOKING);
  const [updateBookingPayment] = useMutation(UPDATE_BOOKING_PAYMENT);

  const setIsSubmitCallback = React.useCallback(value => {
    setIsSubmit(value);
  }, []);

  const [open, setOpen] = React.useState(false);

  const removeEvent = () => {
    return (
      window.removeEventListener('message', handleEventMessage),
      window.removeEventListener('msg', handleEventMessage)
    );
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    resetWidgetForm();
    ReactDOM.unmountComponentAtNode(document.getElementById(WIDGET_ROOT));
    setOpen(false);
    window.parent.document.getElementById(WIDGET_WRAPPER_ID).style.display =
      'none';
  };

  const submitProductCallback = React.useCallback((stripeData, payPalData) => {
    if (stripeData) {
      setStripeData(stripeData);
    } else if (payPalData) {
      enqueueSnackbar(t('Payment has been successfully added'), {
        variant: 'success'
      });
      setPayPalData(payPalData);
    }
  }, []);

  React.useEffect(() => {
    const totalPrice = getWidgetTotalPrice(widgetForm, productData);
    setTotalPrice(totalPrice);

    if (!paymentType) {
      changeFormFieldValue(
        'paymentType',
        getAvailablePaymentsTypes(settingsData)[0]
      );
    }
    handleStripeAuth();
  }, []);

  React.useEffect(() => {
    setPayPalData(null);
    setStripeData(null);
  }, [paymentType]);

  React.useEffect(() => {
    if (!isSubmit) return;

    if (paymentType === PAYPAL) {
      if (!payPalData) {
        enqueueSnackbar(t('Your payment is not added'), {
          variant: 'error'
        });
        setIsSubmit(false);
        return;
      }
      startSubmit();
    } else if (paymentType === PAY_ON_ARRIVAL) {
      startSubmit();
    }
  }, [isSubmit]);

  React.useEffect(() => {
    if (!stripeData || !isSubmit) return;
    startSubmit();
  }, [stripeData]);

  const handleEventMessage = ev => {
    /*if (ev.data.type === 'stripe-3ds-result') {
      const str_auth = document.getElementById('STRIPE_AUTH');
      if (str_auth) str_auth.remove();

      if (ev.data.message.payment_intent) {
        variables.isPaid = true;
        variables.clientSecret = ev.data.message.payment_intent.id;
      }
      updateBookingPayment({ variables: variables }).then(
        res => {
          if (!res?.data?.updateBookingPayment?.success) {
            setIsSubmit(false);

            enqueueSnackbar(t('Your payment is not added'), {
              variant: 'error'
            });
            return;
          }
          handleOpen();
        },
        () => {
          enqueueSnackbar(t('Something was wrong'), {
            variant: 'error'
          });
        }
      );
    } else */
    if (ev.data.msg === 'bif-stripe-complete-event' && clientSecret) {
      const variables = {
        activityId: isAccommodationBooking ? null : productData.generalForm.id,
        accommodationId: isAccommodationBooking
          ? productData.generalForm.id
          : null,
        accommodationBookingId: accommodationBookingId
          ? accommodationBookingId
          : null,
        activityBookingId: activityBookingId ? activityBookingId : null,
        isPaid: false,
        clientSecret: clientSecret
      };
      const str_auth = document.getElementById('STRIPE_AUTH');
      if (str_auth) str_auth.remove();
      variables.isPaid = true;
      updateBookingPayment({ variables: variables }).then(
        res => {
          removeEvent();
          const updateResult = res?.data?.updateBookingPayment;
          if (!updateResult?.success && updateResult?.update) {
            enqueueSnackbar(t('Your payment is not added'), {
              variant: 'error'
            });
            setIsSubmit(false);
            setTimeout(() => {
              handleClose();
            }, 5000);
            return;
          } else if (updateResult?.success) {
            handleOpen();
            return;
          }
        },
        () => {
          enqueueSnackbar(t('Something was wrong'), {
            variant: 'error'
          });
        }
      );
    }
  };
  const handleStripeAuth = () => {
    removeEvent();
    window.addEventListener('message', handleEventMessage, false);
    window.addEventListener('msg', handleEventMessage, false);
  };

  const startSubmit = () => {
    if (!isPrivacy) {
      enqueueSnackbar(
        t('Privacy Policy and Terms & Conditions are not confirmed'),
        {
          variant: 'error'
        }
      );
      setIsSubmit(false);
      return;
    }

    if (isAccommodationBooking) {
      submitAccommodationBooking();
    } else {
      submitActivityBooking();
    }
  };

  const addStripeAuthIframe = (
    nextActionUrl,
    clientSecret,
    activityBookingId,
    accommodationBookingId
  ) => {
    const div = document.createElement('div');
    div.className = 'row-stripe33';
    div.style.cssText =
      'display: block !important;position: fixed !important;z-index: 2147483647 !important;background: rgba(40, 40, 40, 0.75) !important;transition: background 400ms ease 0s !important;will-change: background !important;inset: 0px !important;';
    const button = document.createElement('button');
    button.id = 'close';
    button.position = 'absolute';
    button.textContent = 'Close';
    button.style.cssText =
      'position: absolute;z-index: 99999;margin-top: 3px;left: 846px;ont-size: 20px;width: 65px;height: 35px;background: none;border: none; font-size: 18px;color: white;top: 8%;';
    button.onclick = () => {
      const str_auth = document.getElementById('STRIPE_AUTH');
      document.getElementById('STRIPE_AUTH').style.display = 'none';
      document.getElementById('close').style.display = 'none';
      if (str_auth) str_auth.remove();
      removeEvent();
      handleClose();
    };
    //div.appendChild(button);
    const iframe = document.createElement('iframe');
    div.appendChild(iframe);
    iframe.id = 'STRIPE_AUTH';
    iframe.src = nextActionUrl;
    iframe.setAttribute(
      'sandbox',
      'allow-scripts allow-forms allow-same-origin'
    );
    iframe.width = 600;
    iframe.height = 400;
    iframe.background = 'white';
    iframe.position = 'absolute';
    iframe.width = 930;
    iframe.height = 502;
    iframe.style.cssText +=
      'position:absolute;max-width:82%;max-height:65%;left:9.1%;top:15%;';
    wrapperRef.current.appendChild(div);
  };

  const submitAccommodationBooking = () => {
    setIsSubmit(true);

    const stripeToken = stripeData ? stripeData.id : null;
    const payPalOrderId = payPalData ? payPalData.orderID : null;

    const data = getPreparedBookAccommodationData(
      productData,
      widgetForm,
      [],
      true,
      stripeToken,
      payPalOrderId
    );

    createAccommodationBooking({ variables: data }).then(
      res => {
        const nextActionUrl =
          res?.data?.createAccommodationBooking?.nextActionUrl;
        const clientSecret =
          res?.data?.createAccommodationBooking?.clientSecret;
        const accommodationBookingIdLocal =
          res?.data?.createAccommodationBooking?.id;
        setAccommodationBookingId(accommodationBookingIdLocal);
        setClientSecret(clientSecret);

        if (nextActionUrl) {
          setIsSubmit(false);
          addStripeAuthIframe(
            nextActionUrl,
            clientSecret,
            null,
            accommodationBookingIdLocal
          );
          return;
        } else {
          handleOpen();
        }
      },
      e => {
        setIsSubmit(false);
        if (e.message) {
          const splitErrMessage = e.message.split(':');
          enqueueSnackbar(splitErrMessage[splitErrMessage.length - 1], {
            variant: 'error'
          });
        } else {
          enqueueSnackbar(t('Something was wrong'), {
            variant: 'error'
          });
        }
      }
    );
  };

  const submitActivityBooking = () => {
    setIsSubmit(true);

    const stripeToken = stripeData ? stripeData.id : null;
    const payPalOrderId = payPalData ? payPalData.orderID : null;

    const activityForm = {
      values: {
        ...widgetForm.values,
        accommodations: widgetForm.values.activities
      }
    };
    const data = getPreparedBookActivityData(
      productData,
      activityForm,
      [],
      true,
      stripeToken,
      payPalOrderId
    );

    createActivityBooking({ variables: data }).then(
      res => {
        const activityBookingIdLocal = res?.data?.createActivityBooking?.id;
        const activityCreated = res.data.createActivityBooking;
        setActivityBookingId(activityBookingIdLocal);
        const nextActionUrl = res?.data?.createActivityBooking?.nextActionUrl;
        const clientSecret = res?.data?.createActivityBooking?.clientSecret;
        setClientSecret(clientSecret);

        if (nextActionUrl && activityBookingIdLocal) {
          setIsSubmit(false);
          addStripeAuthIframe(
            nextActionUrl,
            clientSecret,
            activityBookingIdLocal,
            null
          );
          return;
        } else {
          handleOpen();
        }
      },
      e => {
        setIsSubmit(false);
        if (e.message) {
          const splitErrMessage = e.message.split(':');
          enqueueSnackbar(splitErrMessage[splitErrMessage.length - 1], {
            variant: 'error'
          });
        } else {
          enqueueSnackbar(t('Something was wrong'), {
            variant: 'error'
          });
        }
      }
    );
  };

  return (
    <div className={classes.wrapper} ref={wrapperRef}>
      <div className={classes.leftSide}>
        <form onSubmit={handleSubmit} className={classes.form}>
          <Typography variant="h3" classes={{ root: classes.title }}>
            {t('Payment')}
          </Typography>
          <UserInformation />
          <BillingInformation />
          <CreditCardInformation
            settingsData={settingsData}
            submitCallback={submitProductCallback}
            setIsSubmit={setIsSubmitCallback}
            paymentType={paymentType}
            isSubmit={isSubmit}
            currency={currency}
            totalPrice={totalPrice}
            changeFormFieldValue={changeFormFieldValue}
          />
          <button hidden={true} type={'submit'}>
            Submit
          </button>
        </form>
      </div>
      <div className={classes.rightSide}>
        <BookingInformation data={productData} form={widgetForm} />
      </div>

      <BookingConfirmDialog
        open={open}
        handleClose={handleClose}
        title={productData.generalForm.title}
      />
    </div>
  );
};

const mapStateToProps = state => {
  return {
    widgetForm: state.form.widgetForm
  };
};

const mapDispatchToProps = dispatch => ({
  changeFormFieldValue: (field, value) => {
    dispatch(change('widgetForm', field, value));
  },
  resetWidgetForm: () => dispatch(reset('widgetForm'))
});

export default reduxForm({
  form: 'widgetForm',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  touchOnBlur: false,
  validate
})(connect(mapStateToProps, mapDispatchToProps)(Payment));
